<script lang="ts" setup>
import { AbcTable } from "@ordbogen-npm/abc-dsk";
import { computed, ref, watch } from "vue";

import InflectionSchemeCell from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeCell.vue";
import InflectionSchemeTabs from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeTabs.vue";
import {
  makeInflectionSchemes,
  type Tense
} from "@/helpers/dictsys-inflection-scheme/german/verb.helper";
import { useSettingsStore } from "@/stores";
import type { DictsysInflectionSchemeGermanVerbMood } from "@/types/article-formats/dictsys";
import type { Level } from "@/types/dictionary";
import type { InflectionScheme, TabLabel } from "@/types/inflection-scheme.type";

const props = defineProps<{
  inflectionScheme: DictsysInflectionSchemeGermanVerbMood;
}>();

const emits = defineEmits<{
  showTab: [name: string];
}>();

const tenseTabs: TabLabel<Tense>[] = [
  {
    key: "present",
    labels: {
      danish: "Nutid",
      latinAsComment: "Præsens",
      latin: "Præsens"
    }
  },
  {
    key: "simple_past",
    labels: {
      danish: "Datid",
      latinAsComment: "Præteritum",
      latin: "Præteritum"
    }
  },
  {
    key: "present_perfect",
    labels: {
      danish: "Førnutid",
      latinAsComment: "Perfektum",
      latin: "Perfektum"
    }
  },
  {
    key: "past_perfect",
    labels: {
      danish: "Førdatid",
      latinAsComment: "Pluskvamperfektum",
      latin: "Pluskvamperfektum"
    }
  },
  {
    key: "future",
    labels: {
      danish: "Fremtid",
      latinAsComment: "Futurum",
      latin: "Futurum"
    }
  }
];

const settingsStore = useSettingsStore();
const wordClassLevel = computed<Level>(() => settingsStore.activeWordClassLevel);

const schemes = computed<InflectionScheme<Tense>[]>(() =>
  makeInflectionSchemes(props.inflectionScheme, wordClassLevel.value)
);

const tabs = computed<TabLabel<Tense>[]>(() =>
  tenseTabs.filter((tab) => schemes.value.findIndex((schemes) => schemes.key === tab.key) !== -1)
);
const activeTabKey = ref(tabs.value[0].key);

watch(
  () => activeTabKey.value,
  (showTabKey) => {
    emits("showTab", showTabKey);
  }
);
</script>

<template>
  <div>
    <InflectionSchemeTabs
      :tabs="tabs"
      :active-tab="activeTabKey"
      @show-tab="activeTabKey = $event"
    />

    <template v-for="scheme in schemes" :key="scheme.key">
      <AbcTable
        v-if="scheme.key === activeTabKey"
        :rows="scheme.table.rows"
        :column-widths="[{ xs: '105px', sm: undefined }]"
      >
        <template #body-cell="{ cell, isFirst }">
          <!-- First cell of each row is actually the case name -->
          <InflectionSchemeCell v-if="isFirst" :content="cell[0].inflexion" />
          <slot v-else name="inflection-cell" v-bind="{ inflections: cell }">
            <InflectionSchemeCell :content="cell" />
          </slot>
        </template>
      </AbcTable>
    </template>
  </div>
</template>
